import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { auth } from "../services/firebase";
import {
  onAgentIndividuals,
} from "../services/individualService";
import {
  onOrgNotes, onOrgSessions, onOrgIndividuals as onOrgIndividualsSync,
  onAgentNotes, onAgentSessions,
  syncNoteToFirestore, syncNoteTrashToFirestore,
  syncSessionToFirestore, syncIndividualToFirestore,
  syncIndividualDeleteToFirestore, syncOfflineQueue,
} from "../services/firestoreSync";

const LOCAL_STORAGE_KEY = "fieldnotes_local_data";

const defaultData = {
  individuals: [],
  appointments: [],
  completedSessions: [],
  notes: [],
  trash: {
    individuals: [],
    notes: [],
  },
  analytics: {
    totalSessionsAllTime: 0,
    totalMinutesWithClients: 0,
    totalNotesGenerated: 0,
    totalAiGenerationSeconds: 0,
    sessionsPerDay: {},
  },
};

const LocalDataContext = createContext(null);

// Roles that see org-wide data (all individuals, all notes, all sessions)
const ORG_WIDE_ROLES = ["supervisor", "admin"];

// Roles that see their own clinical data (own caseload, own notes)
const CLINICAL_ROLES = ["agent", "clinician", "prescriber"];

// Roles that don't need clinical data listeners
const NON_CLINICAL_ROLES = ["frontdesk", "billing"];

export function LocalDataProvider({ children }) {
  const [data, setData] = useState(defaultData);
  const [loaded, setLoaded] = useState(false);
  const [firestoreReady, setFirestoreReady] = useState(false);
  const unsubsRef = useRef([]);

  // Step 1: Load localStorage cache immediately for instant render
  useEffect(() => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setData(prev => ({ ...defaultData, ...parsed }));
      }
    } catch (e) {
      console.error("[Data] Failed to load localStorage cache:", e);
    }
    setLoaded(true);
  }, []);

  // Step 2: Start Firestore listeners — these are the source of truth
  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        unsubsRef.current.forEach(fn => fn());
        unsubsRef.current = [];
        setFirestoreReady(false);
        return;
      }

      const org = localStorage.getItem("fieldnotes_user_org") || "default-org";
      let role = "agent";
      try {
        const profileCache = localStorage.getItem("fieldnotes_user_profile");
        if (profileCache) {
          const p = JSON.parse(profileCache);
          role = (p.role || "agent").trim();
        }
      } catch (e) {}

      console.log("[Data] Starting Firestore listeners —", user.email, "role:", role, "org:", org);

      // Cleanup existing
      unsubsRef.current.forEach(fn => fn());
      unsubsRef.current = [];

      // Non-clinical roles (frontdesk, billing) don't need clinical data listeners
      if (NON_CLINICAL_ROLES.includes(role)) {
        console.log("[Data] Non-clinical role — skipping clinical data listeners");
        setFirestoreReady(true);
        // Still listen to individuals for frontdesk (they need to see the client list for scheduling)
        if (role === "frontdesk") {
          const unsubInd = onOrgIndividualsSync(org, (inds) => {
            console.log("[Data] Firestore individuals (frontdesk):", inds.length);
            setData(prev => ({ ...prev, individuals: inds }));
          });
          unsubsRef.current = [unsubInd];
        }
        return;
      }

      // --- INDIVIDUALS ---
      let unsubInd;
      if (ORG_WIDE_ROLES.includes(role)) {
        unsubInd = onOrgIndividualsSync(org, (inds) => {
          console.log("[Data] Firestore individuals:", inds.length);
          setData(prev => {
            const firestoreIds = new Set(inds.map(i => i.id));
            const localOnly = prev.individuals.filter(i =>
              !firestoreIds.has(i.id) && i.id.startsWith("ind_")
            );
            return { ...prev, individuals: [...inds, ...localOnly] };
          });
          setFirestoreReady(true);
        });
      } else {
        // agent, clinician, prescriber — see own caseload
        unsubInd = onAgentIndividuals(user.uid, org, (inds) => {
          console.log("[Data] Firestore agent individuals:", inds.length);
          setData(prev => {
            const firestoreIds = new Set(inds.map(i => i.id));
            const localOnly = prev.individuals.filter(i =>
              !firestoreIds.has(i.id) && i.id.startsWith("ind_")
            );
            return { ...prev, individuals: [...inds, ...localOnly] };
          });
          setFirestoreReady(true);
        });
      }

      // --- NOTES (org-wide roles see all, clinical roles see own) ---
      const unsubNotes = ORG_WIDE_ROLES.includes(role)
        ? onOrgNotes(org, noteHandler)
        : onAgentNotes(user.uid, org, noteHandler);

      function noteHandler(firestoreNotes) {
        console.log("[Data] Firestore notes:", firestoreNotes.length, "(role:", role + ")");
        setData(prev => {
          const firestoreIds = new Set(firestoreNotes.map(n => n.id));
          const localOnly = prev.notes.filter(n =>
            !firestoreIds.has(n.id) && n.id.startsWith("note_")
          );
          const converted = firestoreNotes.map(n => ({
            ...n,
            individualName: n.individualName || n.content?.header?.individual || "Unknown",
            date: n.date || (n.createdAt?.toDate ? n.createdAt.toDate().toLocaleDateString() : new Date().toLocaleDateString()),
            serviceType: n.serviceType || n.content?.header?.serviceType || "Session",
            status: n.status || "submitted",
            noteData: n.content || n.noteData || null,
            hasPriorityFlags: n.hasPriorityFlags || false,
          }));
          return { ...prev, notes: [...converted, ...localOnly] };
        });
      }

      // --- SESSIONS (org-wide roles see all, clinical roles see own) ---
      const unsubSessions = ORG_WIDE_ROLES.includes(role)
        ? onOrgSessions(org, sessionHandler)
        : onAgentSessions(user.uid, org, sessionHandler);

      function sessionHandler(firestoreSessions) {
        console.log("[Data] Firestore sessions:", firestoreSessions.length, "(role:", role + ")");
        setData(prev => {
          const firestoreIds = new Set(firestoreSessions.map(s => s.id));
          const localOnly = prev.completedSessions.filter(s =>
            !firestoreIds.has(s.id) && s.id.startsWith("sess_")
          );
          const converted = firestoreSessions.map(s => ({
            ...s,
            completedAt: s.syncedAt?.toDate ? s.syncedAt.toDate().toISOString() : s.completedAt || new Date().toISOString(),
            duration: s.duration || 0,
            individualName: s.individualName || "Unknown",
          }));
          return { ...prev, completedSessions: [...converted, ...localOnly] };
        });
      }

      unsubsRef.current = [unsubInd, unsubNotes, unsubSessions].filter(Boolean);
    });

    return () => {
      unsubAuth();
      unsubsRef.current.forEach(fn => fn());
    };
  }, []);

  // Step 3: Write-through cache to localStorage for offline support
  useEffect(() => {
    if (data && loaded) {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.error("[Data] Failed to save localStorage cache:", e);
      }
    }
  }, [data, loaded]);

  // Sync offline queue when coming back online
  useEffect(() => {
    const handleOnline = () => {
      console.log("[Sync] Back online — flushing offline queue");
      syncOfflineQueue();
    };
    window.addEventListener("online", handleOnline);
    if (navigator.onLine) syncOfflineQueue();
    return () => window.removeEventListener("online", handleOnline);
  }, []);

  // === INDIVIDUALS ===

  const addIndividual = useCallback((individual) => {
    const id = "ind_" + Date.now();
    const fullInd = { id, ...individual };
    setData((prev) => ({
      ...prev,
      individuals: [...prev.individuals, fullInd],
    }));
    syncIndividualToFirestore(fullInd);
    return id;
  }, []);

  const updateIndividual = useCallback((id, updates) => {
    setData((prev) => {
      const updatedInds = prev.individuals.map((ind) =>
        ind.id === id ? { ...ind, ...updates } : ind
      );
      const updatedInd = updatedInds.find(ind => ind.id === id);
      if (updatedInd) syncIndividualToFirestore(updatedInd);
      return { ...prev, individuals: updatedInds };
    });
  }, []);

  const deleteIndividual = useCallback((id) => {
    setData((prev) => {
      const individual = prev.individuals.find((ind) => ind.id === id);
      if (!individual) return prev;
      return {
        ...prev,
        individuals: prev.individuals.filter((ind) => ind.id !== id),
        appointments: prev.appointments.filter((appt) => appt.individualId !== id),
        trash: {
          ...prev.trash,
          individuals: [
            ...prev.trash.individuals,
            {
              ...individual,
              deletedAt: new Date().toISOString(),
              expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            },
          ],
        },
      };
    });
    syncIndividualDeleteToFirestore(id);
  }, []);

  // === NOTES TRASH ===

  const trashNote = useCallback((note) => {
    setData((prev) => ({
      ...prev,
      notes: prev.notes.filter((n) => n.id !== note.id),
      trash: {
        ...prev.trash,
        notes: [
          ...prev.trash.notes,
          {
            ...note,
            deletedAt: new Date().toISOString(),
            expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          },
        ],
      },
    }));
    syncNoteTrashToFirestore(note.id);
  }, []);

  // === NOTES CRUD ===

  const addNote = useCallback((note) => {
    const id = "note_" + Date.now();
    const fullNote = { id, createdAt: new Date().toISOString(), ...note };
    setData((prev) => ({
      ...prev,
      notes: [fullNote, ...prev.notes],
    }));
    syncNoteToFirestore(fullNote);
    return id;
  }, []);

  const updateNote = useCallback((id, updates) => {
    setData((prev) => {
      const updatedNotes = prev.notes.map((n) => (n.id === id ? { ...n, ...updates } : n));
      const updatedNote = updatedNotes.find(n => n.id === id);
      if (updatedNote) syncNoteToFirestore(updatedNote);
      return { ...prev, notes: updatedNotes };
    });
  }, []);

  // === TRASH MANAGEMENT ===

  const restoreIndividual = useCallback((id) => {
    setData((prev) => {
      const item = prev.trash.individuals.find((ind) => ind.id === id);
      if (!item) return prev;
      const { deletedAt, expiresAt, ...restored } = item;
      return {
        ...prev,
        individuals: [...prev.individuals, restored],
        trash: {
          ...prev.trash,
          individuals: prev.trash.individuals.filter((ind) => ind.id !== id),
        },
      };
    });
  }, []);

  const restoreNote = useCallback((id) => {
    setData((prev) => {
      const item = prev.trash.notes.find((n) => n.id === id);
      if (!item) return prev;
      const { deletedAt, expiresAt, ...restored } = item;
      return {
        ...prev,
        notes: [restored, ...prev.notes],
        trash: {
          ...prev.trash,
          notes: prev.trash.notes.filter((n) => n.id !== id),
        },
      };
    });
  }, []);

  const permanentDeleteIndividual = useCallback((id) => {
    setData((prev) => ({
      ...prev,
      trash: {
        ...prev.trash,
        individuals: prev.trash.individuals.filter((ind) => ind.id !== id),
      },
    }));
  }, []);

  const permanentDeleteNote = useCallback((id) => {
    setData((prev) => ({
      ...prev,
      trash: {
        ...prev.trash,
        notes: prev.trash.notes.filter((n) => n.id !== id),
      },
    }));
  }, []);

  const emptyTrash = useCallback(() => {
    setData((prev) => ({
      ...prev,
      trash: { individuals: [], notes: [] },
    }));
  }, []);

  const getTrash = useCallback(() => {
    if (!data) return { individuals: [], notes: [], totalCount: 0 };
    const now = new Date().toISOString();
    const activeIndividuals = (data.trash?.individuals || []).filter(
      (i) => i.expiresAt > now
    );
    const activeNotes = (data.trash?.notes || []).filter(
      (n) => n.expiresAt > now
    );
    return {
      individuals: activeIndividuals,
      notes: activeNotes,
      totalCount: activeIndividuals.length + activeNotes.length,
    };
  }, [data]);

  const getIndividual = useCallback(
    (id) => data?.individuals.find((ind) => ind.id === id) || null,
    [data]
  );

  // === APPOINTMENTS (local-only for now) ===

  const addAppointment = useCallback((appointment) => {
    const id = "appt_" + Date.now();
    setData((prev) => ({
      ...prev,
      appointments: [...prev.appointments, { id, status: "scheduled", ...appointment }],
    }));
    return id;
  }, []);

  const updateAppointment = useCallback((id, updates) => {
    setData((prev) => ({
      ...prev,
      appointments: prev.appointments.map((a) =>
        a.id === id ? { ...a, ...updates } : a
      ),
    }));
  }, []);

  const deleteAppointment = useCallback((id) => {
    setData((prev) => ({
      ...prev,
      appointments: prev.appointments.filter((a) => a.id !== id),
    }));
  }, []);

  const getTodayAppointments = useCallback(() => {
    if (!data) return [];
    const today = new Date().toLocaleDateString();
    return data.appointments
      .filter((appt) => appt.date === today)
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [data]);

  const getUpcomingAppointments = useCallback(() => {
    if (!data) return [];
    const now = new Date();
    const todayStr = now.toLocaleDateString();
    return data.appointments
      .filter((appt) => {
        if (appt.date > todayStr) return true;
        if (appt.date === todayStr && appt.status !== "completed") return true;
        return false;
      })
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.time.localeCompare(b.time);
      });
  }, [data]);

  // === COMPLETED SESSIONS ===

  const recordCompletedSession = useCallback((session) => {
    const dateKey = new Date().toLocaleDateString();
    const sessionRecord = {
      id: "sess_" + Date.now(),
      completedAt: new Date().toISOString(),
      ...session,
    };

    setData((prev) => {
      const newSessions = [...prev.completedSessions, sessionRecord];
      const durationMin = Math.round((session.duration || 0) / 60);
      const aiGenSec = session.aiGenerationTime || 25;
      const sessionsPerDay = { ...prev.analytics.sessionsPerDay };
      sessionsPerDay[dateKey] = (sessionsPerDay[dateKey] || 0) + 1;

      return {
        ...prev,
        completedSessions: newSessions,
        analytics: {
          ...prev.analytics,
          totalSessionsAllTime: prev.analytics.totalSessionsAllTime + 1,
          totalMinutesWithClients: prev.analytics.totalMinutesWithClients + durationMin,
          totalNotesGenerated: prev.analytics.totalNotesGenerated + 1,
          totalAiGenerationSeconds: prev.analytics.totalAiGenerationSeconds + aiGenSec,
          sessionsPerDay,
        },
      };
    });

    syncSessionToFirestore(sessionRecord);
  }, []);

  const recordDraftSession = useCallback((session) => {
    const dateKey = new Date().toLocaleDateString();
    setData((prev) => {
      const durationMin = Math.round((session.duration || 0) / 60);
      const sessionsPerDay = { ...prev.analytics.sessionsPerDay };
      sessionsPerDay[dateKey] = (sessionsPerDay[dateKey] || 0) + 1;

      return {
        ...prev,
        completedSessions: [
          ...prev.completedSessions,
          {
            id: "sess_" + Date.now(),
            completedAt: new Date().toISOString(),
            isDraft: true,
            ...session,
          },
        ],
        analytics: {
          ...prev.analytics,
          totalSessionsAllTime: prev.analytics.totalSessionsAllTime + 1,
          totalMinutesWithClients: prev.analytics.totalMinutesWithClients + durationMin,
          sessionsPerDay,
        },
      };
    });
  }, []);

  const completeAppointment = useCallback((appointmentId) => {
    setData((prev) => ({
      ...prev,
      appointments: prev.appointments.map((appt) =>
        appt.id === appointmentId ? { ...appt, status: "completed" } : appt
      ),
    }));
  }, []);

  // === ANALYTICS ===

  const getAnalytics = useCallback(() => {
    if (!data) return null;

    const sessions = data.completedSessions || [];
    const now = new Date();
    const todayStr = now.toLocaleDateString();
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(now);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    const toDate = (v) => (v instanceof Date ? v : new Date(v));

    const todaySessions = sessions.filter(s => toDate(s.completedAt).toLocaleDateString() === todayStr);
    const weekSessions = sessions.filter(s => toDate(s.completedAt) >= weekAgo);
    const monthSessions = sessions.filter(s => toDate(s.completedAt) >= monthAgo);

    const sumMinutes = (arr) =>
      arr.reduce((acc, s) => acc + Math.round((s.duration || 0) / 60), 0);
    const sumAiSeconds = (arr) =>
      arr.reduce((acc, s) => acc + (s.aiGenerationTime || 25), 0);

    const MANUAL_MINUTES_PER_NOTE = 30;

    const todayMinutes = sumMinutes(todaySessions);
    const weekMinutes = sumMinutes(weekSessions);
    const monthMinutes = sumMinutes(monthSessions);

    const todayAiSec = sumAiSeconds(todaySessions);
    const weekAiSec = sumAiSeconds(weekSessions);
    const monthAiSec = sumAiSeconds(monthSessions);

    const todayManualEst = todaySessions.length * MANUAL_MINUTES_PER_NOTE;
    const weekManualEst = weekSessions.length * MANUAL_MINUTES_PER_NOTE;
    const monthManualEst = monthSessions.length * MANUAL_MINUTES_PER_NOTE;

    const todayTimeSaved = todayManualEst - Math.round(todayAiSec / 60);
    const weekTimeSaved = weekManualEst - Math.round(weekAiSec / 60);
    const monthTimeSaved = monthManualEst - Math.round(monthAiSec / 60);

    const dailyData = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toLocaleDateString();
      const dayLabel = d.toLocaleDateString("en-US", { weekday: "short" });
      const daySessions = sessions.filter(s => toDate(s.completedAt).toLocaleDateString() === dateStr);
      dailyData.push({
        day: dayLabel,
        date: dateStr,
        sessions: daySessions.length,
        minutes: sumMinutes(daySessions),
        aiSeconds: sumAiSeconds(daySessions),
        manualEstimate: daySessions.length * MANUAL_MINUTES_PER_NOTE,
      });
    }

    const byIndividual = {};
    sessions.forEach((s) => {
      const name = s.individualName || "Unknown";
      if (!byIndividual[name]) byIndividual[name] = { sessions: 0, minutes: 0 };
      byIndividual[name].sessions += 1;
      byIndividual[name].minutes += Math.round((s.duration || 0) / 60);
    });

    return {
      today: {
        sessions: todaySessions.length,
        clientMinutes: todayMinutes,
        aiDocSeconds: todayAiSec,
        manualEstMinutes: todayManualEst,
        timeSavedMinutes: Math.max(0, todayTimeSaved),
      },
      week: {
        sessions: weekSessions.length,
        clientMinutes: weekMinutes,
        aiDocSeconds: weekAiSec,
        manualEstMinutes: weekManualEst,
        timeSavedMinutes: Math.max(0, weekTimeSaved),
      },
      month: {
        sessions: monthSessions.length,
        clientMinutes: monthMinutes,
        aiDocSeconds: monthAiSec,
        manualEstMinutes: monthManualEst,
        timeSavedMinutes: Math.max(0, monthTimeSaved),
      },
      allTime: data.analytics,
      dailyData,
      byIndividual,
      avgConfidence: sessions.length > 0
        ? Math.round(sessions.reduce((a, s) => a + (s.confidence || 90), 0) / sessions.length)
        : 0,
      avgSessionMinutes: sessions.length > 0
        ? Math.round(sumMinutes(sessions) / sessions.length)
        : 0,
    };
  }, [data]);

  if (!loaded) return null;

  return (
    <LocalDataContext.Provider
      value={{
        individuals: data?.individuals || [],
        appointments: data?.appointments || [],
        completedSessions: data?.completedSessions || [],
        notes: data?.notes || [],
        firestoreReady,
        addIndividual,
        updateIndividual,
        deleteIndividual,
        getIndividual,
        addAppointment,
        updateAppointment,
        deleteAppointment,
        getTodayAppointments,
        getUpcomingAppointments,
        recordCompletedSession,
        recordDraftSession,
        completeAppointment,
        getAnalytics,
        addNote,
        updateNote,
        trashNote,
        restoreIndividual,
        restoreNote,
        permanentDeleteIndividual,
        permanentDeleteNote,
        emptyTrash,
        getTrash,
        syncAllToFirestore: () => {
          if (data) {
            import("../services/firestoreSync").then(mod => mod.syncAllLocalData(data));
          }
        },
      }}
    >
      {children}
    </LocalDataContext.Provider>
  );
}

export function useLocalData() {
  const context = useContext(LocalDataContext);
  if (!context) {
    throw new Error("useLocalData must be used within a LocalDataProvider");
  }
  return context;
}